import { useMutation } from '@apollo/client';
import { Button, Fields } from '@ets-global/b2c-website-ui';
import { Form, Formik } from 'formik';
import React, { Fragment } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';

import FormLayout from '../../components/Layout/Form/FormLayout';
import { RESET_PASSWORD } from '../../graphql/mutations';
import errorsHandler from '../../helpers/errorsHandler';
import { hasValidLength } from '../../helpers/Validators/PasswordValidator';
import { useAuth, useNotification, useRouter } from '../../hooks';
import useYup from '../../hooks/useYup';

const ResetPassword = () => {
  const {
    currentRoute: { params },
  } = useRouter();
  const { t } = useTranslation();
  const { Yup } = useYup();
  const { newNotification } = useNotification();
  const { handleLogin } = useAuth();

  const [resetPassword] = useMutation(RESET_PASSWORD);

  const validationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .test('hasValidLength', t('common.form.password.min-length'), (value) => hasValidLength(value))
      .required(),
  });

  const onSubmit = async (data, actions) => {
    try {
      const {
        data: {
          resetPassword: { user, token },
        },
      } = await resetPassword({
        variables: {
          password: data.newPassword,
          token: params.token,
        },
      });

      await handleLogin({ user, token });
      newNotification({
        content: t('reset-password.message.success'),
        type: 'success',
      });

      // redirection done by CantAccessLogged HOC
    } catch (error) {
      errorsHandler({ error, actions, newNotification, t });
    }

    actions.setSubmitting(false);
  };

  return (
    <Fragment>
      <Helmet title={t('meta.title.reset-password')} />
      <FormLayout title={t('reset-password.title')} subtitle={t('common.form.password.min-length')}>
        <Formik
          initialValues={{ newPassword: '' }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          render={({ isSubmitting }) => (
            <Form>
              <Fields.FormikFastField
                id={'newPassword'}
                name={'newPassword'}
                component={Fields.PasswordField}
                label={<Trans>reset-password.form.label.password</Trans>}
                autoComplete={'new-password'}
                withStrength={true}
                required={true}
              />
              <Button
                type="submit"
                className="button--full"
                loading={isSubmitting}
                aria-label={t('reset-password.form.label.send')}
              >
                <Trans>reset-password.form.label.send</Trans>
              </Button>
            </Form>
          )}
        />
      </FormLayout>
    </Fragment>
  );
};

export default ResetPassword;
